/**
 * Content helper: every AuraDef reachable through mechanic specs, NPC passives/on-death effects,
 * intermissions and aura hooks (explode/jump). The core registry only walks plain effects, so
 * content appends these to `ContentBundle.auras` to make them resolvable by id.
 */
import type { AuraDef, EffectSpec, EncounterDef, EnemyAbility, MechanicSpec, NpcDef, ZoneEffect } from '../../contracts';

export interface MechanicAuraSources {
  enemyAbilities?: readonly EnemyAbility[];
  npcs?: readonly NpcDef[];
  encounters?: readonly EncounterDef[];
}

export function collectMechanicAuras(src: MechanicAuraSources): AuraDef[] {
  const out = new Map<string, AuraDef>();
  const seen = new Set<object>();

  const aura = (a: AuraDef | undefined): void => {
    if (!a || seen.has(a)) return;
    seen.add(a);
    if (!out.has(a.id)) out.set(a.id, a);
    effects(a.periodic?.effects);
    effects(a.onApply);
    effects(a.onRemove);
    for (const p of a.procs ?? []) effects(p.effects);
    if (a.explodeOnExpire) {
      effects(a.explodeOnExpire.effects);
      zone(a.explodeOnExpire.pool);
    }
    effects(a.jumpOnDispel?.effects);
  };
  const zone = (z: ZoneEffect | undefined | null): void => {
    if (!z) return;
    aura(z.insideAura);
    effects(z.tick?.effects);
  };
  const mechanic = (m: MechanicSpec): void => {
    switch (m.kind) {
      case 'pick':
        effects(m.effects);
        break;
      case 'soak':
        effects(m.effects);
        effects(m.failEffects);
        break;
      case 'hazard':
        zone(m.zone);
        break;
      case 'tether':
        effects(m.failEffects);
        effects(m.breakEffects);
        aura(m.whileTethered);
        break;
      case 'charge':
        effects(m.effects);
        zone(m.trail);
        break;
      case 'gate':
        effects(m.effects);
        effects(m.elseEffects);
        break;
      case 'gaze':
        effects(m.effects);
        break;
      case 'fixate':
        aura(m.aura);
        break;
      case 'healAbsorb':
      case 'shield':
        aura(m.aura);
        break;
      case 'wave':
        effects(m.effects);
        break;
      case 'pull':
      case 'knockback':
        break;
    }
  };
  function effects(list: readonly EffectSpec[] | undefined): void {
    for (const e of list ?? []) {
      switch (e.type) {
        case 'applyAura':
          aura(e.aura);
          break;
        case 'zone':
          zone(e);
          break;
        case 'telegraph':
          effects(e.effects);
          break;
        case 'conditional':
          effects(e.then);
          effects(e.else);
          break;
        case 'mechanic':
          mechanic(e.spec);
          break;
        default:
          break;
      }
    }
  }

  for (const ab of src.enemyAbilities ?? []) {
    effects(ab.effects);
    effects(ab.channelStartEffects);
  }
  for (const n of src.npcs ?? []) {
    for (const a of n.auras ?? []) aura(a);
    effects(n.onDeath);
  }
  for (const enc of src.encounters ?? []) {
    for (const p of enc.phases) {
      effects(p.onEnter);
      const im = p.intermission;
      if (!im) continue;
      aura(im.bossAura);
      effects(im.landing);
      effects(im.onEnd);
      effects(im.onTimeout);
      effects(im.perSurvivingAdd);
    }
  }
  return [...out.values()];
}
